import React, { useState } from 'react';
import { toast } from 'react-hot-toast';
import { Mail, TestTube, RefreshCw, CheckCircle, XCircle, Eye, EyeOff } from 'lucide-react';
import { api } from '../../auth/authStore';
import { SMTPConfig } from '../PlatformSettings';

interface SMTPSettingsFormProps {
  settings: SMTPConfig;
  onUpdate: (updates: Partial<SMTPConfig>) => void;
}

export const SMTPSettingsForm: React.FC<SMTPSettingsFormProps> = ({ settings, onUpdate }) => {
  const [testing, setTesting] = useState(false);
  const [testResult, setTestResult] = useState<{ success: boolean; message: string } | null>(null);
  const [testEmail, setTestEmail] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [passwordChanged, setPasswordChanged] = useState(false);

  // Reset passwordChanged when settings change (e.g., when navigating back to the page)
  React.useEffect(() => {
    if (settings.auth.pass === '••••••••••••••••') {
      setPasswordChanged(false);
    }
  }, [settings.auth.pass]);

  const handleInputChange = (field: string, value: any) => {
    // Track if password field is being changed
    if (field === 'auth.pass') {
      setPasswordChanged(true);
    }

    if (field.includes('.')) {
      const [parent, child] = field.split('.');
      onUpdate({
        [parent]: {
          ...settings[parent as keyof SMTPConfig],
          [child]: value,
        },
      });
    } else {
      onUpdate({ [field]: value });
    }
  };

  const handleTestSMTP = async () => {
    if (!testEmail) {
      toast.error('Please enter a test email address');
      return;
    }

    if (!settings.host || !settings.auth.user || !settings.auth.pass) {
      toast.error('Please fill in all required SMTP settings before testing');
      return;
    }

    try {
      setTesting(true);
      setTestResult(null);

      const response = await api.post('/platform/settings/test-smtp', {
        host: settings.host,
        port: settings.port,
        secure: settings.secure,
        auth: {
          user: settings.auth.user,
          pass: settings.auth.pass,
        },
        from: {
          email: settings.from.email,
          name: settings.from.name,
        },
        testEmail,
      });

      setTestResult(response.data.data);

      if (response.data.success) {
        toast.success('SMTP test successful! Check your email.');
      } else {
        toast.error('SMTP test failed. Check the error message below.');
      }
    } catch (error: any) {
      console.error('Error testing SMTP:', error);
      console.error('Error response:', error.response?.data);

      // Extract error message from the API response format
      const errorMessage = error.response?.data?.error?.message ||
                          error.response?.data?.message ||
                          error.message ||
                          'Failed to test SMTP configuration';

      toast.error(errorMessage);
      setTestResult({
        success: false,
        message: errorMessage,
      });
    } finally {
      setTesting(false);
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center mb-6">
        <Mail className="w-6 h-6 text-blue-500 mr-3" />
        <div>
          <h3 className="text-lg font-semibold text-gray-900">SMTP Configuration</h3>
          <p className="text-sm text-gray-600">Configure email settings for system notifications</p>
        </div>
      </div>

      {/* Enable SMTP */}
      <div className="flex items-center">
        <input
          type="checkbox"
          id="smtp-enabled"
          checked={settings.enabled}
          onChange={(e) => handleInputChange('enabled', e.target.checked)}
          className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
        />
        <label htmlFor="smtp-enabled" className="ml-2 block text-sm text-gray-900">
          Enable SMTP Email Service
        </label>
      </div>

      {settings.enabled && (
        <>
          {/* Quick Setup Buttons */}
          <div className="mb-6">
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Quick Setup
            </label>
            <div className="flex flex-wrap gap-2">
              <button
                type="button"
                onClick={() => {
                  handleInputChange('host', 'smtp.gmail.com');
                  handleInputChange('port', 587);
                  handleInputChange('secure', false); // Use STARTTLS for port 587
                }}
                className="px-3 py-1 text-sm bg-red-100 text-red-700 rounded hover:bg-red-200 transition-colors"
              >
                Gmail
              </button>
              <button
                type="button"
                onClick={() => {
                  handleInputChange('host', 'smtp-mail.outlook.com');
                  handleInputChange('port', 587);
                  handleInputChange('secure', false); // Use STARTTLS for port 587
                }}
                className="px-3 py-1 text-sm bg-blue-100 text-blue-700 rounded hover:bg-blue-200 transition-colors"
              >
                Outlook
              </button>
              <button
                type="button"
                onClick={() => {
                  handleInputChange('host', 'smtp.office365.com');
                  handleInputChange('port', 587);
                  handleInputChange('secure', false); // Use STARTTLS for port 587
                }}
                className="px-3 py-1 text-sm bg-orange-100 text-orange-700 rounded hover:bg-orange-200 transition-colors"
              >
                Office 365
              </button>
              <button
                type="button"
                onClick={() => {
                  handleInputChange('host', 'smtp.gmail.com');
                  handleInputChange('port', 587);
                  handleInputChange('secure', false); // Use STARTTLS for port 587
                }}
                className="px-3 py-1 text-sm bg-purple-100 text-purple-700 rounded hover:bg-purple-200 transition-colors"
              >
                Google Workspace
              </button>
              <button
                type="button"
                onClick={() => {
                  // Try common SMTP settings for custom domains
                  const domain = settings.auth.user.split('@')[1] || 'erpca.com';
                  handleInputChange('host', `mail.${domain}`);
                  handleInputChange('port', 587);
                  handleInputChange('secure', false);
                }}
                className="px-3 py-1 text-sm bg-green-100 text-green-700 rounded hover:bg-green-200 transition-colors"
              >
                Auto-detect Domain
              </button>
            </div>
          </div>

          {/* Server Settings */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                SMTP Host *
              </label>
              <input
                type="text"
                value={settings.host}
                onChange={(e) => handleInputChange('host', e.target.value)}
                placeholder="smtp.gmail.com"
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Port *
              </label>
              <input
                type="number"
                value={settings.port}
                onChange={(e) => handleInputChange('port', parseInt(e.target.value) || 587)}
                placeholder="587"
                min="1"
                max="65535"
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              />
            </div>
          </div>

          {/* Security */}
          <div className="flex items-center">
            <input
              type="checkbox"
              id="smtp-secure"
              checked={settings.secure}
              onChange={(e) => handleInputChange('secure', e.target.checked)}
              className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
            />
            <label htmlFor="smtp-secure" className="ml-2 block text-sm text-gray-900">
              Use SSL/TLS (Enable for port 465, disable for port 587)
            </label>
          </div>

          {/* Authentication */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Username *
              </label>
              <input
                type="text"
                value={settings.auth.user}
                onChange={(e) => handleInputChange('auth.user', e.target.value)}
                placeholder="your-email@gmail.com"
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Password *
                {settings.auth.pass === '••••••••••••••••' && !passwordChanged && (
                  <span className="ml-2 text-xs text-green-600 bg-green-100 px-2 py-1 rounded">
                    Saved
                  </span>
                )}
                {passwordChanged && (
                  <span className="ml-2 text-xs text-orange-600 bg-orange-100 px-2 py-1 rounded">
                    Modified
                  </span>
                )}
              </label>
              <div className="relative">
                <input
                  type={showPassword ? 'text' : 'password'}
                  value={settings.auth.pass}
                  onChange={(e) => handleInputChange('auth.pass', e.target.value)}
                  placeholder={settings.auth.pass === '••••••••••••••••' ? 'Password is saved (enter new password to change)' : 'App password or SMTP password'}
                  className="w-full px-3 py-2 pr-10 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute inset-y-0 right-0 pr-3 flex items-center"
                >
                  {showPassword ? (
                    <EyeOff className="h-4 w-4 text-gray-400" />
                  ) : (
                    <Eye className="h-4 w-4 text-gray-400" />
                  )}
                </button>
              </div>
              {settings.auth.pass === '••••••••••••••••' && !passwordChanged && (
                <p className="mt-1 text-xs text-gray-500">
                  Password is saved and hidden for security. Enter a new password to change it.
                </p>
              )}
            </div>
          </div>

          {/* From Settings */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                From Email *
              </label>
              <input
                type="email"
                value={settings.from.email}
                onChange={(e) => handleInputChange('from.email', e.target.value)}
                placeholder="noreply@erpca.ca"
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                From Name *
              </label>
              <input
                type="text"
                value={settings.from.name}
                onChange={(e) => handleInputChange('from.name', e.target.value)}
                placeholder="ERPCA Platform"
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              />
            </div>
          </div>

          {/* Reply To */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Reply To Email (Optional)
            </label>
            <input
              type="email"
              value={settings.replyTo || ''}
              onChange={(e) => handleInputChange('replyTo', e.target.value)}
              placeholder="support@erpca.ca"
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            />
          </div>

          {/* Test Mode */}
          <div className="flex items-center">
            <input
              type="checkbox"
              id="smtp-test-mode"
              checked={settings.testMode}
              onChange={(e) => handleInputChange('testMode', e.target.checked)}
              className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
            />
            <label htmlFor="smtp-test-mode" className="ml-2 block text-sm text-gray-900">
              Test Mode (Emails will be logged but not sent)
            </label>
          </div>

          {/* Test SMTP */}
          <div className="border-t pt-6">
            <h4 className="text-md font-medium text-gray-900 mb-4">Test SMTP Configuration</h4>
            <div className="flex items-center space-x-4">
              <div className="flex-1">
                <input
                  type="email"
                  value={testEmail}
                  onChange={(e) => setTestEmail(e.target.value)}
                  placeholder="Enter test email address"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                />
              </div>
              <button
                onClick={handleTestSMTP}
                disabled={testing || !testEmail}
                className="px-4 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600 disabled:opacity-50 flex items-center"
              >
                {testing ? (
                  <RefreshCw className="w-4 h-4 animate-spin mr-2" />
                ) : (
                  <TestTube className="w-4 h-4 mr-2" />
                )}
                {testing ? 'Testing...' : 'Send Test Email'}
              </button>
            </div>

            {testResult && (
              <div className={`mt-4 p-4 rounded-lg ${
                testResult.success ? 'bg-green-50 border border-green-200' : 'bg-red-50 border border-red-200'
              }`}>
                <div className="flex items-start">
                  {testResult.success ? (
                    <CheckCircle className="w-5 h-5 text-green-500 mr-2 mt-0.5" />
                  ) : (
                    <XCircle className="w-5 h-5 text-red-500 mr-2 mt-0.5" />
                  )}
                  <div>
                    <p className={`font-medium ${
                      testResult.success ? 'text-green-800' : 'text-red-800'
                    }`}>
                      {testResult.success ? 'Test Successful' : 'Test Failed'}
                    </p>
                    <p className={`text-sm mt-1 ${
                      testResult.success ? 'text-green-700' : 'text-red-700'
                    }`}>
                      {testResult.message}
                    </p>
                  </div>
                </div>
              </div>
            )}
          </div>

          {/* Help Text */}
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
            <h5 className="font-medium text-blue-900 mb-2">SMTP Configuration Tips:</h5>
            <ul className="text-sm text-blue-800 space-y-2">
              <li>
                <strong>For Gmail & Google Workspace:</strong>
                <ul className="ml-4 mt-1 space-y-1">
                  <li>• Host: <code className="bg-blue-100 px-1 rounded">smtp.gmail.com</code></li>
                  <li>• Port: <code className="bg-blue-100 px-1 rounded">587</code></li>
                  <li>• Enable SSL/TLS: <code className="bg-blue-100 px-1 rounded">Yes</code></li>
                  <li>• Username: Your full email address (@gmail.com or custom domain)</li>
                  <li>• Password: Use an <strong>App Password</strong> (not your regular password)</li>
                  <li>• <a href="https://support.google.com/accounts/answer/185833" target="_blank" className="text-blue-600 underline">How to create App Password</a></li>
                  <li>• <strong>Google Workspace:</strong> Works with custom domains like @erpca.com</li>
                </ul>
              </li>
              <li>
                <strong>For Outlook/Hotmail:</strong>
                <ul className="ml-4 mt-1 space-y-1">
                  <li>• Host: <code className="bg-blue-100 px-1 rounded">smtp-mail.outlook.com</code></li>
                  <li>• Port: <code className="bg-blue-100 px-1 rounded">587</code></li>
                  <li>• Enable SSL/TLS: <code className="bg-blue-100 px-1 rounded">Yes</code></li>
                </ul>
              </li>
              <li>
                <strong>For Custom Domains (like @erpca.com):</strong>
                <ul className="ml-4 mt-1 space-y-1">
                  <li>• Contact your domain/hosting provider for SMTP settings</li>
                  <li>• Common patterns: <code className="bg-blue-100 px-1 rounded">mail.yourdomain.com</code> or <code className="bg-blue-100 px-1 rounded">smtp.yourdomain.com</code></li>
                  <li>• Try the "Auto-detect Domain" button above</li>
                  <li>• Alternative: Use Gmail SMTP with a Gmail account for testing</li>
                </ul>
              </li>
              <li>• <strong>Important:</strong> Don't use "localhost" unless you have a local SMTP server running</li>
              <li>• <strong>Username/Password mismatch:</strong> Make sure your username matches the SMTP provider (Gmail username for Gmail SMTP, etc.)</li>
              <li>• Always test your configuration before enabling</li>
            </ul>
          </div>
        </>
      )}
    </div>
  );
};
